import { keepPreviousData, useQuery } from '@tanstack/react-query'
import type { QueryClient } from '@tanstack/react-query'
import type { MonthlyBudgetStatus, PayoutReadyItem, PayoutSummary } from '@ayub/shared'
import { PayoutStatus } from '@ayub/shared'
import { PAYOUTS_PAGE_SIZE, fetchPayouts, fetchReadyPayouts } from './api'
import type { PayoutListResponse } from './api'
import { fetchBudgetStatus } from './budgetApi'

/**
 * Uchala tab yorlig'idagi sonlar (`docs/03-app-flow.md`: "So'rovlar (3)", "To'lovga tayyor
 * (18)") — HAR QANDAY faol tabdan mustaqil, bitta umumiy so'rov bilan olinadi. Naqsh
 * `components/bloggers/hooks.ts`dagi `useBloggerStageCounts()` bilan bir xil: `pageSize: 1`
 * bilan so'rov yuboriladi, faqat hisoblagichlar kerak.
 *
 * Hisoblagichlar javobda TEKIS joylashadi (`api.ts`dagi izohga qarang) — shuning uchun
 * kerakli maydonlar shu yerda ajratib olinadi.
 */
export function usePayoutSummary() {
  const query = useQuery<PayoutSummary, Error>({
    queryKey: ['payouts', 'summary'],
    queryFn: async () => {
      const response = await fetchPayouts({ page: 1, pageSize: 1 })
      return {
        pendingRequests: response.pendingRequests,
        pendingRequestsAmount: response.pendingRequestsAmount,
        readyBloggers: response.readyBloggers,
        readyAmount: response.readyAmount,
        blockedByMissingCard: response.blockedByMissingCard,
      }
    },
    placeholderData: keepPreviousData,
  })

  return {
    summary: query.data,
    isLoading: query.isLoading,
    error: query.isError ? query.error : null,
  }
}

/** "So'rovlar" tabi — `status: REQUESTED`, sahifalangan. */
export function usePayoutRequests(page: number) {
  const query = useQuery<PayoutListResponse, Error>({
    queryKey: ['payouts', 'list', PayoutStatus.REQUESTED, page],
    queryFn: () =>
      fetchPayouts({ status: PayoutStatus.REQUESTED, page, pageSize: PAYOUTS_PAGE_SIZE }),
    placeholderData: keepPreviousData,
  })

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.isError ? query.error : null,
    refetch: query.refetch,
  }
}

/**
 * "Tarix" tabi — faqat yakunlangan holatlar (`PAID`/`CANCELLED`), hech qachon `REQUESTED`
 * ko'rsatilmaydi (u "So'rovlar" tabida). Standart — `PAID`, chunki "o'tgan to'lovlar" deganda
 * admin birinchi navbatda haqiqatan to'langanlarni ko'rmoqchi bo'ladi; bekor qilinganlar
 * ikkinchi chip orqali ko'riladi (`docs/03` bu ikkalasini alohida chip sifatida chizmagan —
 * qaror shu yerda qabul qilingan, "Verify" hisobotiga qarang).
 */
export type HistoryStatusFilter = typeof PayoutStatus.PAID | typeof PayoutStatus.CANCELLED

export function usePayoutHistory(status: HistoryStatusFilter, page: number) {
  const query = useQuery<PayoutListResponse, Error>({
    queryKey: ['payouts', 'history', status, page],
    queryFn: () => fetchPayouts({ status, page, pageSize: PAYOUTS_PAGE_SIZE }),
    placeholderData: keepPreviousData,
  })

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.isError ? query.error : null,
    refetch: query.refetch,
  }
}

/**
 * Joriy oy byudjet holati (Byudjet paneli va "To'lovga tayyor" cheklovi uchun). To'lovdan
 * keyin `invalidatePayoutRelatedCaches` (pastda, 'budget' ham qamrab olingan) yangilaydi —
 * "qolgan byudjet" darhol kamayadi.
 */
export function useBudget() {
  const query = useQuery<MonthlyBudgetStatus, Error>({
    queryKey: ['budget', 'status'],
    queryFn: () => fetchBudgetStatus(),
    placeholderData: keepPreviousData,
  })

  return {
    budget: query.data,
    isLoading: query.isLoading,
    error: query.isError ? query.error : null,
  }
}

/** "To'lovga tayyor" tabi — sahifalanmagan to'liq ro'yxat. */
export function usePayoutReady() {
  const query = useQuery<PayoutReadyItem[], Error>({
    queryKey: ['payouts', 'ready'],
    queryFn: fetchReadyPayouts,
  })

  return {
    items: query.data ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.isError ? query.error : null,
    refetch: query.refetch,
  }
}

/**
 * Har qanday to'lov mutatsiyasidan keyin (yaratish/ommaviy/to'landi/bekor) eskiradigan
 * keshlar — vazifa talabi: "invalidate the payouts, dashboard, videos and bloggers caches".
 * Sabab: to'lov balansni, video bosqichini (`PAID`) va Dashboard hisoblagichlarini birdan
 * o'zgartiradi — birortasi eskirib qolsa admin ishonchi yo'qoladi ("stale 'To'lovga tayyor:
 * 1.2 mln' to'lagandan keyin ham").
 */
export function invalidatePayoutRelatedCaches(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey[0]
      return (
        key === 'payouts' ||
        key === 'dashboard' ||
        key === 'videos' ||
        key === 'bloggers' ||
        key === 'budget'
      )
    },
  })
}
